import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import useAdminData from "@/lib/useAdminData";
import { AdminHeader, Card, Btn, StatusBadge, Empty, ErrorBanner, Loading } from "@/components/admin/ui";
import { formatINR } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, AlertTriangle } from "lucide-react";

export default function AdminProducts() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  // Last successfully loaded catalogue is kept on screen if a refresh fails — a
  // failed request must never look like "0 products".
  const { data, loading, refreshing, error, stale, reload } = useAdminData(
    () => api.get("/admin/products").then((r) => r.data.products)
  );
  const products = data ?? [];

  useEffect(() => { reload(); }, [reload]);

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <AdminHeader title="Products" subtitle={`${products.length} products`}
        action={<Btn variant="accent" onClick={() => navigate("/admin/products/new")} data-testid="add-product-btn"><Plus className="h-4 w-4" /> Add Product</Btn>} />
      {error && (
        <ErrorBanner className="mb-4" message={error.message} stale={stale} onRetry={reload} />
      )}
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" data-testid="product-search"
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400" />
      </div>
      {loading && !data && !error ? <Loading text="Loading products…" /> : (
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {refreshing && <div className="text-[11px] text-slate-400 px-3 pt-2" data-testid="products-refreshing">Refreshing…</div>}
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="p-3 font-semibold">Product</th>
                <th className="p-3 font-semibold">Price</th>
                <th className="p-3 font-semibold">Stock</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold">Flags</th>
                <th className="p-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`product-row-${p.slug}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <img src={resolveMediaUrl(p.images?.[0])} alt="" onError={handleMediaError} className="h-10 w-10 rounded-lg bg-slate-50 object-contain" />
                      <span className="font-medium text-slate-900 line-clamp-1 max-w-[220px]">{p.name}</span>
                    </div>
                  </td>
                  <td className="p-3 font-mono">{formatINR(p.price)}</td>
                  <td className="p-3 font-mono">{p.totalStock}</td>
                  <td className="p-3"><StatusBadge status={p.status} /></td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {p.featured && <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">Featured</span>}
                      {p.trending && <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">Trending</span>}
                      {p.newArrival && <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">New</span>}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => navigate(`/admin/products/${p.id}/edit`)} className="p-2 text-slate-500 hover:text-amber-600" data-testid={`edit-product-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => del(p.id, p.name)} className="p-2 text-slate-500 hover:text-red-600" data-testid={`delete-product-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Genuine empty state only: backend succeeded AND really returned no rows. */}
          {!error && !loading && filtered.length === 0 && <Empty text="No products found" />}
          {error && filtered.length === 0 && (
            <p className="text-xs text-slate-500 px-3 py-4 flex items-center gap-2" data-testid="products-load-failed-note">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              Product list could not be loaded. This is not an empty catalogue — retry above.
            </p>
          )}
        </div>
      </Card>
      )}
    </div>
  );
}
